import React from 'react'
import { toChartY } from '../utils'

export type NudgeChartEvent = {
  id: string
  type: 'sent' | 'replied' | 'recovered'
  at: string
}

interface ActivityChartProps {
  allNudgeEvents: NudgeChartEvent[]
  nudgeSentCount: number
}

export function ActivityChart({ allNudgeEvents, nudgeSentCount }: ActivityChartProps) {
  const [chartRange, setChartRange] = React.useState<'4w' | '8w' | '12w'>('8w')

  const accent = 'var(--accent)'
  const chartBucketsCount = chartRange === '4w' ? 4 : chartRange === '12w' ? 12 : 8

  const chartStats = React.useMemo(() => {
    const now = Date.now()
    const bucketMs = 7 * 24 * 60 * 60 * 1000
    const start = now - chartBucketsCount * bucketMs
    const buckets = Array.from({ length: chartBucketsCount }, (_, i) => {
      const from = start + i * bucketMs
      const to = from + bucketMs
      const inBucket = (e: NudgeChartEvent) => {
        const t = new Date(e.at).getTime()
        return t >= from && t < to
      }
      return {
        sent: allNudgeEvents.filter(e => e.type === 'sent' && inBucket(e)).length,
        replied: allNudgeEvents.filter(e => e.type === 'replied' && inBucket(e)).length,
        recovered: allNudgeEvents.filter(e => e.type === 'recovered' && inBucket(e)).length,
        label: `W${i + 1}`,
      }
    })

    const width = 665
    const maxY = Math.max(
      1,
      ...buckets.map(b => b.sent),
      ...buckets.map(b => b.replied),
      ...buckets.map(b => b.recovered),
    )
    const toX = (idx: number) => (buckets.length <= 1 ? 0 : (idx / (buckets.length - 1)) * width)
    const sentPoints    = buckets.map((b, i) => ({ x: toX(i), y: toChartY(b.sent,      maxY) }))
    const repliedPoints = buckets.map((b, i) => ({ x: toX(i), y: toChartY(b.replied,   maxY) }))
    const recovPoints   = buckets.map((b, i) => ({ x: toX(i), y: toChartY(b.recovered, maxY) }))
    const toLine  = (pts: { x: number; y: number }[]) => pts.map(p => `${p.x},${p.y}`).join(' ')
    const toArea  = (line: string) => `${line} ${width},105 0,105`
    const sentLine    = toLine(sentPoints)
    const repliedLine = toLine(repliedPoints)
    const recovLine   = toLine(recovPoints)
    return {
      buckets,
      sentPoints, repliedPoints, recovPoints,
      sentLine, repliedLine, recovLine,
      sentArea: toArea(sentLine),
      repliedArea: toArea(repliedLine),
      recovArea: toArea(recovLine),
    }
  }, [allNudgeEvents, chartBucketsCount])

  const hasRecovered = allNudgeEvents.some(e => e.type === 'recovered')

  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <div className="panel-title">Nudge Activity</div>
          <div className="panel-sub">
            {chartRange === '4w' ? 'Last 4 weeks' : chartRange === '12w' ? 'Last 12 weeks' : 'Last 8 weeks'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="chart-legend">
            <div className="legend-item">
              <div className="legend-dot" style={{ background: 'var(--blue)' }} />
              Sent
            </div>
            <div className="legend-item">
              <div className="legend-dot" style={{ background: accent }} />
              Replied
            </div>
            {hasRecovered ? (
              <div className="legend-item">
                <div className="legend-dot" style={{ background: 'var(--green)' }} />
                Recovered
              </div>
            ) : null}
          </div>
          <select
            className="select-input"
            value={chartRange}
            onChange={e => setChartRange(e.target.value as '4w' | '8w' | '12w')}
            style={{ minWidth: 130 }}
          >
            <option value="4w">Last 4 weeks</option>
            <option value="8w">Last 8 weeks</option>
            <option value="12w">Last 12 weeks</option>
          </select>
        </div>
      </div>

      <div className={`chart-wrap ${nudgeSentCount === 0 ? 'chart-wrap-empty' : ''}`}>
        <svg
          className={`chart-svg ${nudgeSentCount === 0 ? 'chart-svg-empty' : ''}`}
          height="120"
          viewBox="0 0 760 120"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="gb" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#5b9cf6" stopOpacity=".25" />
              <stop offset="100%" stopColor="#5b9cf6" stopOpacity="0" />
            </linearGradient>
          </defs>
          <line x1="0" y1="10" x2="760" y2="10" stroke="var(--chart-grid)" strokeWidth="1" />
          <line x1="0" y1="50" x2="760" y2="50" stroke="var(--chart-grid)" strokeWidth="1" />
          <line x1="0" y1="90" x2="760" y2="90" stroke="var(--chart-grid)" strokeWidth="1" />
          {chartStats.buckets.map((b, i) => (
            <text
              key={`wk-${b.label}-${i}`}
              x={chartStats.sentPoints[i]?.x ?? 0}
              y="116"
              fontSize="10"
              fill="var(--chart-label)"
              fontFamily="monospace"
              textAnchor="middle"
            >
              {b.label}
            </text>
          ))}
          <polygon points={chartStats.sentArea} fill="url(#gb)" />
          <polyline points={chartStats.sentLine} fill="none" stroke="#5b9cf6" strokeWidth="2" />
          <polygon points={chartStats.repliedArea} fill={accent} fillOpacity=".15" />
          <polyline points={chartStats.repliedLine} fill="none" stroke={accent} strokeWidth="2" />
          {hasRecovered ? (
            <>
              <polygon points={chartStats.recovArea} fill="var(--green)" fillOpacity=".12" />
              <polyline points={chartStats.recovLine} fill="none" stroke="var(--green)" strokeWidth="2" />
              {chartStats.recovPoints.length > 0 ? (
                <circle
                  cx={chartStats.recovPoints[chartStats.recovPoints.length - 1]?.x}
                  cy={chartStats.recovPoints[chartStats.recovPoints.length - 1]?.y}
                  r="4"
                  fill="var(--green)"
                />
              ) : null}
            </>
          ) : null}
          {chartStats.sentPoints.length > 0 ? (
            <circle
              cx={chartStats.sentPoints[chartStats.sentPoints.length - 1]?.x}
              cy={chartStats.sentPoints[chartStats.sentPoints.length - 1]?.y}
              r="4"
              fill="#5b9cf6"
            />
          ) : null}
          {chartStats.repliedPoints.length > 0 ? (
            <circle
              cx={chartStats.repliedPoints[chartStats.repliedPoints.length - 1]?.x}
              cy={chartStats.repliedPoints[chartStats.repliedPoints.length - 1]?.y}
              r="4"
              fill={accent}
            />
          ) : null}
        </svg>
        {nudgeSentCount === 0 ? (
          <div className="chart-empty-overlay">
            <div className="chart-empty-title">No data available</div>
            <div className="chart-empty-sub">Messages have not been sent yet.</div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
